import type { Message, Participant, Artifact, AgentName, Stage } from '../types'
import { AGENT_DISPLAY_NAMES, STAGE_AGENTS, STAGE_NAMES } from '../types'
import { getHandoffIntro, getHandoffOutro } from '../agents/prompts'
import { sendMessage, advanceStage, revertStage, completeSession, createReflection, updateReflectionSynthesis, setAiThinking as setAiThinkingFirestore, addArtifact } from './session'
import { authHeaders } from './auth'
import { FUNCTIONS_BASE_URL } from './functionsBase'

interface LullDetectorConfig {
  lullThresholdMs: number // default 10000 (10s)
  maxContextMessages: number // default 30
}

const DEFAULT_CONFIG: LullDetectorConfig = {
  lullThresholdMs: 5000,
  maxContextMessages: 30,
}

/**
 * A streaming message doc is finalized by the same request that created it, so a
 * `streaming: true` flag older than this is a leftover from a request that died
 * mid-stream and must not block the lull detector forever.
 */
const STREAM_STALE_MS = 120000

function isStreamingNow(m: Message): boolean {
  if (!m.streaming) return false
  const ms = typeof m.timestamp?.toMillis === 'function' ? m.timestamp.toMillis() : 0
  if (!ms) return true
  return Date.now() - ms < STREAM_STALE_MS
}

/** A failed /chat call, carrying the server's own message when it is meant for the user. */
class ChatRequestError extends Error {
  userMessage?: string

  constructor(message: string, userMessage?: string) {
    super(message)
    this.name = 'ChatRequestError'
    this.userMessage = userMessage
  }
}

export class AIOrchestrator {
  private sessionId: string
  private lullTimer: ReturnType<typeof setTimeout> | null = null
  private isProcessing = false
  private config: LullDetectorConfig
  private isReturning = false
  private isSolo = false
  private reflectionJustCreated = false
  private postHandoffFollowUp = false
  reflectionActive = false
  public lastAgentMessageTime = 0
  private lastErrorTime = 0
  public onProcessingChange?: (processing: boolean) => void

  constructor(sessionId: string, config?: Partial<LullDetectorConfig>) {
    this.sessionId = sessionId
    this.config = { ...DEFAULT_CONFIG, ...config }
  }

  setIsReturning(value: boolean) {
    this.isReturning = value
  }

  setIsSolo(value: boolean) {
    this.isSolo = value
  }

  setReflectionActive(active: boolean) {
    this.reflectionActive = active
  }

  private setProcessing(value: boolean) {
    this.isProcessing = value
    this.onProcessingChange?.(value)
    // Broadcast thinking state to all participants via Firestore
    setAiThinkingFirestore(this.sessionId, value).catch(() => {})
  }

  // Called whenever messages or typing state changes
  onActivity(
    messages: Message[],
    participants: Participant[],
    currentStage: Stage,
    currentAgent: AgentName,
    artifacts: Artifact[],
  ) {
    // Clear existing timer
    if (this.lullTimer) {
      clearTimeout(this.lullTimer)
      this.lullTimer = null
    }

    // Don't trigger if already processing
    if (this.isProcessing) return

    // Don't trigger AI while a reflection is active. Users are filling in the panel
    if (this.reflectionActive) return

    // After a reflection was just created, suppress the next AI call
    // so the agent doesn't respond while users fill in the reflection panel
    if (this.reflectionJustCreated) {
      this.reflectionJustCreated = false
      return
    }


    // Check if anyone is typing
    const anyoneTyping = participants.some(p => p.isTyping)
    if (anyoneTyping) return

    // Don't trigger while the server is still appending tokens to an agent message.
    // Without this, a participant typing mid-stream could kick off a second call.
    if (messages.some(isStreamingNow)) return

    // Check if the last message was from the agent (don't respond to yourself)
    const lastMsg = messages[messages.length - 1]
    if (!lastMsg) return
    if (lastMsg.participantId === 'agent') {
      // Exception: after a cross-agent handoff, allow an automatic follow-up
      // so the new agent can do its first real work (e.g., Builder drafting a prompt)
      if (this.postHandoffFollowUp) {
        this.postHandoffFollowUp = false
        this.lullTimer = setTimeout(() => {
          this.triggerResponse(messages, participants, currentStage, currentAgent, artifacts)
        }, 10000)
        return
      }
      return
    }

    // Check if there's user activity (messages or artifact uploads) since last agent message
    const lastAgentIdx = [...messages].reverse().findIndex(m => m.participantId === 'agent')
    const lastUserActivity = [...messages].reverse().findIndex(
      m => m.participantId !== 'agent' && m.participantId !== 'system' && (m.type === 'message' || m.type === 'artifact')
    )
    if (lastUserActivity > lastAgentIdx && lastAgentIdx !== -1) {
      // No new user activity since last agent response - don't trigger
      // Actually this means user activity is MORE recent, which is what we want
    }

    // Use a longer delay for artifact uploads to allow batch uploading,
    // but show the thinking indicator right away so users know docs are being processed
    const isArtifactUpload = lastMsg.type === 'artifact'
    // A solo session is single-player: no need to leave room for teammates to chime in
    const baseDelay = this.isSolo ? 2500 : this.config.lullThresholdMs
    const delay = isArtifactUpload ? 8000 : baseDelay

    if (isArtifactUpload) {
      setAiThinkingFirestore(this.sessionId, true).catch(() => {})
      this.onProcessingChange?.(true)
    }

    // Set lull timer
    this.lullTimer = setTimeout(() => {
      this.triggerResponse(messages, participants, currentStage, currentAgent, artifacts)
    }, delay)
  }

  // Force immediate response (e.g., @mention or session start)
  async triggerImmediate(
    messages: Message[],
    participants: Participant[],
    currentStage: Stage,
    currentAgent: AgentName,
    artifacts: Artifact[],
  ) {
    if (this.lullTimer) {
      clearTimeout(this.lullTimer)
      this.lullTimer = null
    }
    await this.triggerResponse(messages, participants, currentStage, currentAgent, artifacts)
  }

  private async triggerResponse(
    messages: Message[],
    participants: Participant[],
    currentStage: Stage,
    currentAgent: AgentName,
    artifacts: Artifact[],
  ) {
    if (this.isProcessing) return
    this.setProcessing(true)

    try {
      // The system prompt, team context and handoff briefing are assembled by the
      // chat function from the session's own state. The client no longer supplies them.

      // Get recent messages for context (include artifacts so AI knows about uploads)
      const recentMessages = messages
        .filter(m => m.visible && (m.type === 'message' || m.type === 'artifact'))
        .slice(-this.config.maxContextMessages)
        .map(m => ({
          role: m.participantId === 'agent' ? 'agent' : 'user',
          displayName: m.displayName,
          content: m.type === 'artifact'
            ? `[uploaded file: ${m.content}]`
            : m.content,
        }))

      // Get relevant artifacts, deduplicating by content
      const seenContent = new Set<string>()
      const artifactData: { fileName: string; content: string }[] = []
      for (const a of artifacts) {
        const contentKey = a.content.slice(0, 200)
        if (!seenContent.has(contentKey)) {
          seenContent.add(contentKey)
          artifactData.push({ fileName: a.fileName, content: a.content })
        }
      }

      const response = await fetch(`${FUNCTIONS_BASE_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          sessionId: this.sessionId,
          currentStage,
          currentAgent,
          recentMessages,
          artifacts: artifactData,
          // The server streams the reply straight into Firestore message docs.
          stream: true,
        }),
      })

      if (!response.ok) {
        let serverError = ''
        try {
          const body = await response.json()
          if (typeof body?.error === 'string') serverError = body.error
        } catch {
          // Non-JSON error body: fall back to the generic apology.
        }
        // Quota and capacity rejections carry copy written for the user; everything
        // else is an internal failure the user should not be shown verbatim.
        throw new ChatRequestError(
          `AI response failed: ${response.status}${serverError ? ` ${serverError}` : ''}`,
          response.status === 429 && serverError ? serverError : undefined,
        )
      }

      const data = await response.json()
      const agentDisplayName = AGENT_DISPLAY_NAMES[currentAgent]
      const isReflectionTurn = !!data.hasReflectionTrigger && !data.shouldAdvanceStage

      // When streaming, the server already wrote the agent's message docs and hands
      // back their ids. A reply with no messageIds means the pre-streaming function is
      // still deployed, so write the messages from here exactly as we used to.
      if (!Array.isArray(data.messageIds)) {
        await this.writeAgentMessages(data, currentAgent, isReflectionTurn)
      }

      // Handle reflection trigger: create the reflection immediately so the popup
      // appears right away instead of waiting for user confirmation
      if (isReflectionTurn) {
        // Use the server-extracted prompt, falling back to a generic one
        const prompt = data.reflectionPrompt || 'Please share your thoughts independently.'

        // Wait 4 seconds so users can read the agent's chat message before
        // the reflection UI appears (the isProcessing lock prevents other AI calls)
        await new Promise(resolve => setTimeout(resolve, 4000))

        await createReflection(this.sessionId, prompt, currentStage)
        this.reflectionJustCreated = true
      }

      this.lastAgentMessageTime = Date.now()

      // Save any files the agent created as artifacts
      if (data.createdFiles && data.createdFiles.length > 0) {
        for (const file of data.createdFiles) {
          const ext = file.fileName.split('.').pop() || 'txt'
          await addArtifact(this.sessionId, agentDisplayName, file.fileName, file.content, ext)
          await sendMessage(this.sessionId, 'agent', agentDisplayName, file.fileName, 'artifact', currentAgent)
        }
      }

      // Handle session completion (Stage 5 Scout signals end)
      if (data.sessionComplete) {
        await completeSession(this.sessionId)
      }

      // Handle agent-initiated stage advancement (after message is sent)
      if (data.shouldAdvanceStage && !data.hasReflectionTrigger && currentStage < 5) {
        const nextStage = (currentStage + 1) as Stage
        const nextAgent = STAGE_AGENTS[nextStage]
        // Delay so the user can read the advancement message before handoff begins
        setTimeout(() => {
          this.performHandoff(currentAgent, nextAgent, nextStage, messages, participants, artifacts)
        }, 6000)
      }
    } catch (error) {
      console.error('AI orchestration error:', error)
      // Send a system message about the error (with 30s cooldown)
      const now = Date.now()
      if (now - this.lastErrorTime > 30000) {
        this.lastErrorTime = now
        // Quota and capacity rejections come with copy meant for the user; show it
        // instead of the generic apology so people know why the agent stopped.
        const quotaMessage = error instanceof ChatRequestError ? error.userMessage : undefined
        await sendMessage(
          this.sessionId,
          'system',
          'System',
          quotaMessage
            ? quotaMessage
            : this.isSolo
              ? 'The agent hit a snag. Send another message and it will pick right back up.'
              : 'The AI agent is taking a moment to think... please continue the conversation.',
          'system',
        )
      }
    } finally {
      this.setProcessing(false)
    }
  }

  /**
   * Legacy fallback for a chat function that has not been updated to stream: split the
   * reply on [MSG_BREAK] and post the message docs from here, one per part. The streaming
   * server writes these itself, so this runs only during a deploy window.
   */
  private async writeAgentMessages(
    data: { content?: string },
    currentAgent: AgentName,
    isReflectionTurn: boolean,
  ) {
    const raw = data.content || ''
    const agentDisplayName = AGENT_DISPLAY_NAMES[currentAgent]

    // Safety net: strip markers client-side in case the server missed any
    const stripMarkers = (text: string) =>
      text.replace(/\[REFLECTION_TRIGGER\][\s\S]*?\[\/REFLECTION_TRIGGER\]/g, '').replace(/\[REFLECTION_TRIGGER\]/g, '').replace(/\[\/REFLECTION_TRIGGER\]/g, '').replace(/\[ADVANCE_STAGE\]/g, '').replace(/\[MSG_BREAK\]/g, '').trim()

    if (isReflectionTurn) {
      // One message before the reflection panel opens (only if non-empty after stripping)
      const content = stripMarkers(raw)
      if (content.trim()) {
        await sendMessage(this.sessionId, 'agent', agentDisplayName, content, 'message', currentAgent)
      }
      return
    }

    // Regular message: split into chunks if MSG_BREAK was present
    const chunks = (raw.includes('[MSG_BREAK]') ? raw.split('[MSG_BREAK]') : [raw])
      .map(chunk => stripMarkers(chunk))
      .filter(chunk => chunk.trim().length > 0)

    for (let i = 0; i < chunks.length; i++) {
      if (i > 0) {
        // Delay between chunks to feel conversational
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
      await sendMessage(this.sessionId, 'agent', agentDisplayName, chunks[i], 'message', currentAgent)
    }
  }

  async performHandoff(
    fromAgent: AgentName,
    toAgent: AgentName,
    newStage: Stage,
    messages: Message[],
    _participants: Participant[],
    _artifacts: Artifact[],
  ) {
    this.setProcessing(true)

    try {
      const fromName = AGENT_DISPLAY_NAMES[fromAgent]
      const toName = AGENT_DISPLAY_NAMES[toAgent]
      const sameAgent = fromAgent === toAgent

      if (!sameAgent) {
        // Send outro message from outgoing agent
        const outro = getHandoffOutro(fromAgent, toAgent)
        if (outro) {
          await sendMessage(this.sessionId, 'agent', fromName, outro, 'message', fromAgent)
        }

        // The function owns the briefing: it writes it to the session doc.
        const conversationSummary = messages
          .filter(m => m.visible)
          .slice(-50)
          .map(m => `${m.displayName}: ${m.content}`)
          .join('\n')

        const summaryResponse = await fetch(`${FUNCTIONS_BASE_URL}/generateHandoffSummary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({
            fromAgent: fromName,
            toAgent: toName,
            sessionId: this.sessionId,
            conversationSummary,
          }),
        })

        if (summaryResponse.ok) {
          await sendMessage(
            this.sessionId,
            'system',
            'System',
            `\u{1F4CB} ${fromName} is briefing ${toName}...`,
            'system',
          )
        }
      }

      // Advance the stage
      await advanceStage(this.sessionId, newStage, toAgent)

      // Send system message about transition
      const stageMsg = sameAgent
        ? `Stage ${newStage} has begun. ${toName} is continuing the conversation.`
        : `Stage ${newStage} has begun. ${toName} is joining the conversation.`
      await sendMessage(this.sessionId, 'system', 'System', stageMsg, 'system')

      // Send intro from new agent (only for different agents)
      if (!sameAgent) {
        const intro = getHandoffIntro(toAgent, newStage, this.isReturning)
        await sendMessage(this.sessionId, 'agent', toName, intro, 'message', toAgent)

        // Schedule automatic follow-up so the new agent can do its first
        // real work (e.g., Builder drafting a prompt). This bypasses the
        // normal lull detector which won't fire after an agent message.
        this.postHandoffFollowUp = true
      }

      // Hold processing lock briefly so the lull detector doesn't
      // immediately fire a new AI response, which gives people time to read
      if (sameAgent) {
        await new Promise(resolve => setTimeout(resolve, 8000))
      }
    } catch (error) {
      console.error('Handoff error:', error)
    } finally {
      this.setProcessing(false)
    }
  }

  async performRevert(
    targetStage: Stage,
    _messages: Message[],
    _participants: Participant[],
    _artifacts: Artifact[],
  ) {
    this.setProcessing(true)

    try {
      const targetAgent = STAGE_AGENTS[targetStage]
      const agentDisplayName = AGENT_DISPLAY_NAMES[targetAgent]


      // Revert the stage in Firestore
      await revertStage(this.sessionId, targetStage, targetAgent)

      // Send system message about the revert
      await sendMessage(
        this.sessionId,
        'system',
        'System',
        `The session has returned to Stage ${targetStage}: ${STAGE_NAMES[targetStage]}`,
        'system',
      )

      // Send a brief re-intro from the appropriate agent
      await sendMessage(
        this.sessionId,
        'agent',
        agentDisplayName,
        `Looks like we're heading back. What would you like to revisit?`,
        'message',
        targetAgent,
      )
    } catch (error) {
      console.error('Revert error:', error)
    } finally {
      this.setProcessing(false)
    }
  }

  async synthesizeReflections(
    agentName: AgentName,
    reflectionId: string,
    prompt: string,
    responses: Record<string, string>,
    participants: Participant[],
  ) {
    this.setProcessing(true)
    try {
      const teamContext = participants
        .map(p => `- ${p.displayName}`)
        .join('\n')

      // Map participant IDs to names
      const namedResponses: Record<string, string> = {}
      for (const [pid, content] of Object.entries(responses)) {
        const participant = participants.find(p => p.id === pid)
        namedResponses[participant?.displayName || pid] = content
      }

      const response = await fetch(`${FUNCTIONS_BASE_URL}/synthesizeReflections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          sessionId: this.sessionId,
          agentName: AGENT_DISPLAY_NAMES[agentName],
          prompt,
          responses: namedResponses,
          teamContext: `Team members:\n${teamContext}`,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        const messageId = await sendMessage(
          this.sessionId,
          'agent',
          AGENT_DISPLAY_NAMES[agentName],
          data.synthesis,
          'message',
          agentName,
        )
        // Write synthesisMessageId back to prevent re-triggering
        await updateReflectionSynthesis(this.sessionId, reflectionId, messageId)
        // Schedule automatic follow-up so the agent continues after synthesis
        // (without this, the lull detector won't fire because the last message is from the agent)
        this.postHandoffFollowUp = true
      }
    } catch (error) {
      console.error('Synthesis error:', error)
    } finally {
      this.setProcessing(false)
    }
  }

  destroy() {
    if (this.lullTimer) {
      clearTimeout(this.lullTimer)
      this.lullTimer = null
    }
  }
}
